import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Container,
	Row,
	Col,
	Card,
	CardBody,
	Table,
	Button,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Form,
	FormGroup,
	Label,
	Input,
	Badge,
	Spinner,
	UncontrolledDropdown,
	DropdownToggle,
	DropdownMenu,
	DropdownItem,
	Alert,
} from 'reactstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { BASE_URL } from '../constants/apiConfig';
import './ManageUsers.css';


const ManageUsers = () => {
	const navigate = useNavigate();
	const [users, setUsers] = useState([]);
	const [filteredUsers, setFilteredUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchKeyword, setSearchKeyword] = useState('');
	const [roleFilter, setRoleFilter] = useState('all');

	// Modal states
	const [showAddModal, setShowAddModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showMessageModal, setShowMessageModal] = useState(false); // For success/error messages
	const capitalizeFirst = (str) => {
		if (!str) return 'Viewer';
		return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
	};
	// Form state
	const [formData, setFormData] = useState({
		firstName: '',
		middleName: '',
		lastName: '',
		fullName: '',
		email: '',
		role: 'Viewer',
		password: '',
		confirmPassword: '',
		status: 'Active',
		reviewRound: 'Round 1',
	});

	const [selectedUser, setSelectedUser] = useState(null);
	const [message, setMessage] = useState('');
	const [messageType, setMessageType] = useState('success'); // 'success' or 'error'

	// Add this state at the top of your component
	const [userStats, setUserStats] = useState({
		total: 0,
		admin: 0,
		editor: 0,
		reviewer: 0,
		viewer: 0
	});
	// Role definitions with permissions
	const roles = {
		Admin: {
			label: 'Admin',
			color: 'danger',
			permissions: [
				'View applications & documents',
				'Server-side search/sort/filter',
				'Create personal dashboards',
				'Share dashboards',
				'Configure scoring criteria',
				'Enter/Update scores',
				'Mandatory final remarks only',
				'Change category',
				'Change application status'
			],
		},
		Editor: {
			label: 'Editor',
			color: 'primary',
			permissions: [
				'View applications & documents',
				'Server-side search/sort/filter',
				'Create personal dashboards',
				'Share dashboards',
				'Enter/Update scores',
				'Mandatory final remarks only',
				'Change category (if granted)',
				'Change application status (limited)'
			],
		},
		Reviewer: {
			label: 'Reviewer',
			color: 'info',
			permissions: [
				'View assigned & permitted applications & documents',
				'Server-side search/sort/filter (on assigned set)',
				'Create personal dashboards',
				'Enter/Update scores (assigned only)',
				'Mandatory final remarks only'
			],
		},
		Viewer: {
			label: 'Viewer',
			color: 'secondary',
			permissions: [
				'View applications & documents',
				'Server-side search/sort/filter',
				'Create personal dashboards'
			],
		},
	};


	// Fetch users
	const fetchUsers = async () => {
		try {
			setLoading(true);
			const response = await axios.get(`${BASE_URL}/api/admin/users`, {
				params: { excludeRole: 'applicant' },
				headers: {
					'Authorization': `Bearer ${sessionStorage.getItem('token')}`
				}
			});

			console.log('API Response:', response.data); // Add this line to debug

			if (response.data && response.data.success) {
				const users = response.data.users || []; // Changed from response.data.data to response.data.users
				setUsers(users);

			}
		} catch (error) {
			console.error('Error fetching users:', error);
			toast.error('Failed to fetch users');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchUsers();
	}, []);

	// Apply filters
	useEffect(() => {
		let filtered = [...users];

		// Role filter
		if (roleFilter !== 'all') {
			filtered = filtered.filter((user) => user.role === roleFilter.toLowerCase());
		}

		// Keyword search
		if (searchKeyword.trim()) {
			const keyword = searchKeyword.toLowerCase();
			filtered = filtered.filter(
				(user) =>
					user.name?.toLowerCase().includes(keyword) ||
					user.email?.toLowerCase().includes(keyword)
			);
		}

		setFilteredUsers(filtered);
	}, [searchKeyword, roleFilter, users]);

	// Handle form input change
	const handleInputChange = (e) => {
		const { name, value } = e.target;
		const updatedData = {
			...formData,
			[name]: value,
		};
		
		// Auto-construct fullName when firstName, middleName, or lastName changes
		if (name === 'firstName' || name === 'middleName' || name === 'lastName') {
			const first = name === 'firstName' ? value : updatedData.firstName;
			const middle = name === 'middleName' ? value : updatedData.middleName;
			const last = name === 'lastName' ? value : updatedData.lastName;
			
			const nameParts = [first, middle, last].filter(part => part && part.trim());
			updatedData.fullName = nameParts.join(' ');
		}
		
		// Auto-select 'Round 1' when role changes to Reviewer
		if (name === 'role' && value === 'Reviewer') {
			updatedData.reviewRound = 'Round 1';
		}
		
		setFormData(updatedData);
	};

	// Handle add user
	const handleAddUser = async (e) => {
		e.preventDefault();

		// Validation
		if (!formData.firstName || !formData.lastName || !formData.email) {
			toast.warning('First name, last name, and email are required');
			return;
		}

		// Validate passwords if provided
		if (formData.password || formData.confirmPassword) {
			if (formData.password !== formData.confirmPassword) {
				toast.warning('Password and Confirm Password do not match');
				return;
			}
			if (formData.password.length < 8) {
				toast.warning('Password must be at least 8 characters long');
				return;
			}
		}

		try {
			const token = sessionStorage.getItem('token');
			const userData = {
				firstName: formData.firstName,
				middleName: formData.middleName,
				lastName: formData.lastName,
				fullName: formData.fullName,
				email: formData.email,
				role: formData.role,
				status: formData.status
			};

			// Only include password if provided by admin
			if (formData.password) {
				userData.password = formData.password;
			}

			// Add reviewRound only if role is Reviewer
			if (formData.role === 'Reviewer') {
				userData.reviewRound = formData.reviewRound;
			}

			const response = await axios.post(`${BASE_URL}/api/admin/createuser`, userData,
				{
					headers: {
						Authorization: `Bearer ${token}`,
						'Content-Type': 'application/json'
					}
				}
			);
			if (response.data.success) {
				setMessage('User added successfully');
				setMessageType('success');
				setShowMessageModal(true);
				setShowAddModal(false);
				resetForm();
				fetchUsers();
			} else {
				setMessage('Failed to add user');
				setMessageType('error');
				setShowMessageModal(true);
			}
		} catch (error) {
			const backendMessage =
				error.response?.data?.message || "An unexpected error occurred";

			setMessage(backendMessage);
			setMessageType("error");
			setShowMessageModal(true);
		}
	};

	// Handle edit user
	const handleEditUser = async (e) => {
		e.preventDefault();

		try {
			const token = sessionStorage.getItem('token');
			const updateData = {
				fullName: formData.fullName,
				firstName: formData.firstName,
				middleName: formData.middleName,
				lastName: formData.lastName,
				email: formData.email,
				role: formData.role,
				status: formData.status === 'Active' ? true : false,
			};

			// Add reviewRound only if role is Reviewer
			if (formData.role === 'Reviewer') {
				updateData.reviewRound = formData.reviewRound;
			}

			// Only include password if it's been changed
			if (formData.password) {
				updateData.password = formData.password;
			}

			const response = await axios.put(
				`${BASE_URL}/api/admin/${selectedUser.id}`,
				updateData,
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			if (response.data.success) {
				setMessage('User updated successfully');
				setMessageType('success');
				setShowMessageModal(true);
				setShowEditModal(false);
				resetForm();
				fetchUsers();
			}
		} catch (error) {
			console.error('Error updating user:', error);
			setMessage(error.response?.data?.message || 'Failed to update user');
			setMessageType('error');
			setShowMessageModal(true);
		}
	};

	// Handle status toggle (activate/deactivate)
	const handleStatusToggle = async (user) => {
		try {
			const token = sessionStorage.getItem('token');
			const newStatus = !user.status; // Toggle boolean status

			const response = await axios.put(
				`${BASE_URL}/api/admin/users/${user.id}/status`,
				{ status: newStatus },
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			if (response.data.success) {
				setMessage(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
				setMessageType('success');
				setShowMessageModal(true);
				fetchUsers();
			}
		} catch (error) {
			console.error('Error updating user status:', error);
			setMessage(error.response?.data?.message || 'Failed to update user status');
			setMessageType('error');
			setShowMessageModal(true);
		}
	};

	// Open edit modal
	const openEditModal = async (user) => {
		setSelectedUser(user);
		try {
			const token = sessionStorage.getItem('token');
			const response = await axios.get(
				`${BASE_URL}/api/admin/users/${user.id}`,
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			if (response.data.success) {
				const userData = response.data.user;
				setFormData({
					fullName: userData.name || '',
					firstName: userData.first_name || '',
					middleName: userData.middle_name || '',
					lastName: userData.last_name || '',
					email: userData.email || '',
					role: capitalizeFirst(userData.role) || 'Viewer',
					//password: '',
					status: userData.status === 1 ? 'Active' : 'Inactive',
					reviewRound: userData.review_round || 'Both',
				});
			}
		} catch (error) {
			console.error('Error fetching user details:', error);
			toast.error('Failed to fetch user details');
			// Fallback to basic data
			setFormData({
				fullName: user.name || '',
				firstName: '',
				middleName: '',
				lastName: '',
				email: user.email || '',
				role: capitalizeFirst(user.role) || 'Viewer',
				//password: '',
				status: user.status === 1 ? 'Active' : 'Inactive',
				reviewRound: user.review_round || 'Both',
			});
		}
		setShowEditModal(true);
	};


	// Reset form
	const resetForm = () => {
		setFormData({
			firstName: '',
			middleName: '',
			lastName: '',
			fullName: '',
			email: '',
			role: 'Viewer',
			password: '',
			confirmPassword: '',
			status: 'Active',
			reviewRound: 'Both',
		});
		setSelectedUser(null);
	};

	// Get role badge color
	const getRoleBadge = (role, reviewRound) => {
		const roleInfo = roles[role] || roles.Viewer;
		return (
			<div className="d-flex flex-column gap-1">
				<Badge color={roleInfo.color}>{roleInfo.label}</Badge>
				{role === 'Reviewer' && reviewRound && (
					<Badge color="warning" className="small">
						{reviewRound}
					</Badge>
				)}
			</div>
		);
	};

	// Get status badge
	const getStatusBadge = (status) => {
		const isActive = status;
		//const isActive = status === 1 || status === '1' || status === 'Active';
		const statusText = isActive ? 'Active' : 'Inactive';
		return (
			<Badge color={isActive ? 'success' : 'danger'}>
				{statusText}
			</Badge>
		);
	};

	return (
		<Container fluid className="container">
			{/* Header */}
			<Row className="mb-4">
				<Col>
					<h2 className="page-title">Manage Users</h2>
					<p className="text-muted">Add, edit, and manage user access and roles</p>
				</Col>
				<Col xs="auto" className="d-flex gap-2">
					<Button color="secondary" onClick={() => navigate('/admin/dashboard')}>
						Back
					</Button>
				<Button color="primary" onClick={() => {
					resetForm();
					setShowAddModal(true);
				}}>
					Add New User
				</Button>
				</Col>
			</Row>


			{/* Role Statistics */}
			<Row className="mb-4">
				{Object.entries(roles).map(([key, role]) => {
					const count = users.filter(user =>
						user.role && user.role.toLowerCase() === key.toLowerCase()).length;
					//const count = users.filter((u) => u.role === key).length;
					return (
						<Col md={3} key={key}>
							<Card className="role-stat-card">
								<CardBody>
									<div className="d-flex justify-content-between align-items-center">
										<div>
											<h6 className="text-muted mb-1">{role.label}</h6>
											<h3 className="mb-0">{count}</h3>
										</div>
										<Badge color={role.color} className="role-badge-large">
											{role.label}
										</Badge>
									</div>
								</CardBody>
							</Card>
						</Col>
					);
				})}
			</Row>

			{/* Filters */}
			<Row className="mb-3">
				<Col md={4}>
					<Input
						type="text"
						placeholder="Search by name or email..."
						value={searchKeyword}
						onChange={(e) => setSearchKeyword(e.target.value)}
						className="search-input"
					/>
				</Col>
				<Col md={3}>
					<Input
						type="select"
						value={roleFilter}
						onChange={(e) => setRoleFilter(e.target.value)}
						className="filter-select">
						<option value="all">All Roles</option>
						{Object.keys(roles).map((role) => (
							<option key={role} value={role}>
								{role}
							</option>
						))}
					</Input>
				</Col>
				<Col md={5} className="text-end">
					<span className="text-muted">
						Showing {filteredUsers.length} of {users.length} users
					</span>
				</Col>
			</Row>

			{/* Users Table */}
			<Row>
				<Col>
					<Card>
						<CardBody className="p-0">
							{loading ? (
								<div className="text-center p-5">
									<Spinner color="primary" />
									<p className="mt-3">Loading users...</p>
								</div>
							) : (
								<div className="table-responsive">
									<Table hover className="users-table mb-0">
										<thead>
											<tr>
												<th>Name</th>
												<th>Email</th>
												<th>Role</th>
												<th>Status</th>
												<th>Created Date</th>
												<th>Last Login</th>
												<th>Actions</th>
											</tr>
										</thead>
										<tbody>
											{filteredUsers.length === 0 ? (
												<tr>
													<td colSpan="7" className="text-center p-5">
														<div className="no-data">
															<p>No users found</p>
														</div>
													</td>
												</tr>
											) : (
												filteredUsers.map((user) => (
												<tr key={user.id}>
													<td>
														<strong>{user.name}</strong>
													</td>
													<td>{user.email}</td>
													<td>{getRoleBadge(capitalizeFirst(user.role), user.review_round)}</td>
													<td>{getStatusBadge(user.status)}</td>
													<td>
															{user.createdAt
																? new Date(user.createdAt).toLocaleDateString()
																: '-'}
														</td>
														<td>
															{user.lastLogin
																? new Date(user.lastLogin).toLocaleString()
																: 'Never'}
														</td>
														<td>
															<UncontrolledDropdown>
																<DropdownToggle color="light" size="sm" caret>
																	Actions
																</DropdownToggle>
																<DropdownMenu end>
																	<DropdownItem onClick={() => openEditModal(user)}>
																		Edit User
																	</DropdownItem>
																	<DropdownItem
																		onClick={() => handleStatusToggle(user)}>
																		{user.status ? 'Deactivate' : 'Activate'}
																	</DropdownItem>
																</DropdownMenu>
															</UncontrolledDropdown>
														</td>
													</tr>
												))
											)}
										</tbody>
									</Table>
								</div>
							)}
						</CardBody>
					</Card>
				</Col>
			</Row>

			{/* Add User Modal */}
			<Modal isOpen={showAddModal} toggle={() => setShowAddModal(false)} size="lg">
				<ModalHeader toggle={() => setShowAddModal(false)}>Add New User</ModalHeader>
				<Form onSubmit={handleAddUser}>
					<ModalBody>
						<Row>
							<Col md={4}>
								<FormGroup>
									<Label for="firstName">
										First Name <span className="text-danger">*</span>
									</Label>
									<Input
										type="text"
										id="firstName"
										name="firstName"
										value={formData.firstName}
										onChange={handleInputChange}
										placeholder="Enter first name"
										required
									/>
								</FormGroup>
							</Col>
							<Col md={4}>
								<FormGroup>
									<Label for="middleName">Middle Name</Label>
									<Input
										type="text"
										id="middleName"
										name="middleName"
										value={formData.middleName}
										onChange={handleInputChange}
										placeholder="Optional"
									/>
								</FormGroup>
							</Col>
							<Col md={4}>
								<FormGroup>
									<Label for="lastName">
										Last Name <span className="text-danger">*</span>
									</Label>
									<Input
										type="text"
										id="lastName"
										name="lastName"
										value={formData.lastName}
										onChange={handleInputChange}
										placeholder="Enter last name"
										required
									/>
								</FormGroup>
							</Col>
						</Row>
						<Row>
							<Col md={12}>
								<FormGroup>
									<Label for="fullName">Full Name (Auto-generated)</Label>
									<Input
										type="text"
										id="fullName"
										name="fullName"
										value={formData.fullName}
										readOnly
										disabled
										placeholder="Full name will be generated automatically"
									/>
									<small className="text-muted">This field is automatically constructed from first, middle, and last name</small>
								</FormGroup>
							</Col>
						</Row>
						<Row className="mt-3">
							<Col md={8}>
								<FormGroup>
									<Label for="email">
										Email <span className="text-danger">*</span>
									</Label>
									<Input
										type="email"
										id="email"
										name="email"
										value={formData.email}
										onChange={handleInputChange}
										required
									/>
								</FormGroup>
							</Col>
					</Row>
					<Row className="mt-3">
						<Col md={6}>
							<FormGroup>
								<Label for="password">Password (Optional)</Label>
								<Input
									type="password"
									id="password"
									name="password"
									value={formData.password}
									onChange={handleInputChange}
									placeholder="Leave empty for auto-generated password"
								/>
								<small className="text-muted">Min 8 characters. Leave blank to generate random password.</small>
							</FormGroup>
						</Col>
						<Col md={6}>
							<FormGroup>
								<Label for="confirmPassword">Confirm Password</Label>
								<Input
									type="password"
									id="confirmPassword"
									name="confirmPassword"
									value={formData.confirmPassword}
									onChange={handleInputChange}
									placeholder="Confirm password"
									disabled={!formData.password}
								/>
							</FormGroup>
						</Col>
					</Row>
					<Row className="mt-3">
						<Col md={4}>
							<FormGroup>
								<Label for="role">Role</Label>
								<Input
									type="select"
									id="role"
									name="role"
									value={formData.role}
									onChange={handleInputChange}>
									{Object.keys(roles).map((role) => (
										<option key={role} value={role}>
											{role}
										</option>
									))}
								</Input>
							</FormGroup>
						</Col>
					</Row>
					{formData.role === 'Reviewer' && (
							<Row className="mt-3">
								<Col md={12}>
									<FormGroup>
										<Label for="reviewRound">
											Review Round Assignment <span className="text-danger">*</span>
										</Label>
										<Input
											type="select"
											id="reviewRound"
											name="reviewRound"
											value={formData.reviewRound}
											onChange={handleInputChange}>
											<option value="Round 1">Round 1 Only</option>
											<option value="Round 2">Round 2 Only</option>
										</Input>
										<small className="text-muted">
											Specify which review round(s) this reviewer can participate in
										</small>
									</FormGroup>
								</Col>
							</Row>
						)}
						<Row>
							<Col>
								<div className="role-permissions-info">
									<h6 className="mb-2">Role Permissions:</h6>
									<ul className="permissions-list">
										{roles[formData.role].permissions.map((perm, index) => (
											<li key={index}>{perm}</li>
										))}
									</ul>
								</div>
							</Col>
						</Row>
					</ModalBody>
					<ModalFooter>
						<Button color="secondary" onClick={() => setShowAddModal(false)}>
							Cancel
						</Button>
						<Button color="primary" type="submit">
							Add User
						</Button>
					</ModalFooter>
				</Form>
			</Modal>

			{/* Edit User Modal */}
			<Modal isOpen={showEditModal} toggle={() => setShowEditModal(false)} size="lg">
				<ModalHeader toggle={() => setShowEditModal(false)}>Edit User</ModalHeader>
				<Form onSubmit={handleEditUser}>
					<ModalBody>
						<Row>
							<Col md={4}>
								<FormGroup>
									<Label for="edit-firstName">First Name</Label>
									<Input
										type="text"
										id="edit-firstName"
										name="firstName"
										value={formData.firstName}
										onChange={handleInputChange}
										required
									/>
								</FormGroup>
							</Col>
							<Col md={4}>
								<FormGroup>
									<Label for="edit-middleName">Middle Name</Label>
									<Input
										type="text"
										id="edit-middleName"
										name="middleName"
										value={formData.middleName}
										onChange={handleInputChange}
										placeholder="Optional"
									/>
								</FormGroup>
							</Col>
							<Col md={4}>
								<FormGroup>
									<Label for="edit-lastName">Last Name</Label>
									<Input
										type="text"
										id="edit-lastName"
										name="lastName"
										value={formData.lastName}
										onChange={handleInputChange}
										required
									/>
								</FormGroup>
							</Col>
						</Row>
						<Row>
							<Col md={6}>
								<FormGroup>
									<Label for="edit-fullName">Full Name</Label>
									<Input
										type="text"
										id="edit-fullName"
										name="fullName"
										value={formData.fullName}
										onChange={handleInputChange}
										required
									/>
								</FormGroup>
							</Col>
							<Col md={6}>
								<FormGroup>
									<Label for="edit-email">Email</Label>
									<Input
										type="email"
										id="edit-email"
										name="email"
										value={formData.email}
										onChange={handleInputChange}
										required
									/>
								</FormGroup>
							</Col>
						</Row>
						<Row>
							{/* <Col md={6}>
								<FormGroup>
									<Label for="edit-password">
										New Password <small className="text-muted">(Leave blank to keep current)</small>
									</Label>
									<Input
										type="password"
										id="edit-password"
										name="password"
										value={formData.password}
										onChange={handleInputChange}
										placeholder="Enter new password"
									/>
								</FormGroup>
							</Col> */}
							<Col md={6}>
								<FormGroup>
									<Label for="edit-role">Role</Label>
									<Input
										type="select"
										id="edit-role"
										name="role"
										value={formData.role}
										onChange={handleInputChange}>
										{Object.keys(roles).map((role) => (
											<option key={role} value={role}>
												{role}
											</option>
										))}
									</Input>
								</FormGroup>
							</Col>

							<Col md={6}>
								<FormGroup>
									<Label for="edit-status">Status</Label>
									<Input
										type="select"
										id="edit-status"
										name="status"
										value={formData.status}
										onChange={handleInputChange}>
										<option value="Active">Active</option>
										<option value="Inactive">Inactive</option>
									</Input>
								</FormGroup>
							</Col>
						</Row>
						{formData.role === 'Reviewer' && (
							<Row className="mt-3">
								<Col md={12}>
									<FormGroup>
										<Label for="edit-reviewRound">
											Review Round Assignment <span className="text-danger">*</span>
										</Label>
										<Input
											type="select"
											id="edit-reviewRound"
											name="reviewRound"
											value={formData.reviewRound}
											onChange={handleInputChange}>
											<option value="Round 1">Round 1 Only</option>
											<option value="Round 2">Round 2 Only</option>
										</Input>
										<small className="text-muted">
											Specify which review round(s) this reviewer can participate in
										</small>
									</FormGroup>
								</Col>
							</Row>
						)}
						<Row>
							<Col>
								<div className="role-permissions-info">
									<h6 className="mb-2">Role Permissions:</h6>
									<ul className="permissions-list">
										{roles[formData.role].permissions.map((perm, index) => (
											<li key={index}>{perm}</li>
										))}
									</ul>
								</div>
							</Col>
						</Row>
					</ModalBody>
					<ModalFooter>
						<Button color="secondary" onClick={() => setShowEditModal(false)}>
							Cancel
						</Button>
						<Button color="primary" type="submit">
							Update User
						</Button>
					</ModalFooter>
				</Form>
			</Modal>



			{/* Message Modal */}
			<Modal isOpen={showMessageModal} toggle={() => setShowMessageModal(false)} centered>
				<ModalBody>
					<Alert color={messageType === 'success' ? 'success' : 'danger'}>
						{message}
					</Alert>
				</ModalBody>
				<ModalFooter>
					<Button variant="secondary" onClick={() => setShowMessageModal(false)}>
						Close
					</Button>
				</ModalFooter>
			</Modal>
		</Container>
	);
};

export default ManageUsers;
